/**
 * Contact Page Component - Collapsible Section
 * All content is in DOM for SEO/AdSense but starts collapsed for clean UI
 */

import { useState, useEffect } from 'react'
import { Mail, CheckCircle, XCircle, ExternalLink, Lightbulb, Phone, MessageCircle, ChevronDown, ChevronUp } from 'lucide-react'
import '../contactPage.css'

const ContactPage = () => {
  const [isExpanded, setIsExpanded] = useState(false)

  // Auto-expand if URL hash matches or if scrolled to from footer
  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === '#contact') {
        setIsExpanded(true)
      }
    }

    // Check on mount
    handleHashChange()

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange)
    
    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [])

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded)
    
    // Scroll to section when expanding
    if (!isExpanded) {
      setTimeout(() => {
        const element = document.getElementById('contact')
        if (element) {
          const offset = 80
          const elementPosition = element.getBoundingClientRect().top
          const offsetPosition = elementPosition + window.pageYOffset - offset

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          })
        }
      }, 100)
    }
  }

  return (
    <section className="contact-page-section" id="contact">
      <div className="contact-container">
        
        {/* Collapsible Header - Always Visible */}
        <div className="contact-header-collapsible" onClick={toggleExpanded}>
          <div className="contact-header-content">
            <Mail size={32} color="#ff6b35" />
            <div className="header-text">
              <h2>Contact Us</h2>
              <p className="tagline">We'd love to hear from you</p>
            </div>
          </div>
          <button 
            className="toggle-button"
            aria-label={isExpanded ? 'Collapse contact section' : 'Expand contact section'}
          >
            {isExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
          </button>
        </div>

        {/* Summary Preview - Always Visible */}
        <div className="contact-summary-preview">
          <p className="summary-preview-text">
            Have questions, feedback, or suggestions? Reach out at <strong>contact@zerodebtjourney.com</strong>
          </p>
        </div>

        {/* Expandable Content */}
        <div className={`contact-content-expandable ${isExpanded ? 'expanded' : 'collapsed'}`}>
          
          <div className="contact-content">
            
            {/* Section 1: Get in Touch */}
            <article className="contact-section main-contact-section">
              <div className="section-header">
                <MessageCircle size={28} color="#ff6b35" />
                <h3>Get in Touch</h3>
              </div>
              <div className="contact-card">
                <div className="contact-method-main">
                  <Mail size={24} color="#ff6b35" />
                  <div className="contact-details">
                    <h4>Email Us</h4>
                    <a href="mailto:contact@zerodebtjourney.com" className="email-link">
                      contact@zerodebtjourney.com
                    </a>
                    <p className="response-time">
                      <strong>Response Time:</strong> We typically respond within 24-48 hours 
                      (usually faster!)
                    </p>
                  </div>
                </div>
                <p className="personal-note">
                  We read and respond to every message personally. Your feedback helps us 
                  improve this tool for everyone.
                </p>
              </div>
            </article>

            {/* Section 2: What We Can Help With */}
            <article className="contact-section">
              <div className="section-header">
                <CheckCircle size={28} color="#10b981" />
                <h3>What We Can Help With</h3>
              </div>
              <div className="help-grid">
                <div className="help-item">
                  <CheckCircle size={20} color="#10b981" />
                  <div>
                    <h4>Calculator Questions</h4>
                    <ul>
                      <li>How to use specific features</li>
                      <li>Understanding your results</li>
                      <li>Technical issues or bugs</li>
                      <li>Browser compatibility problems</li>
                    </ul>
                  </div>
                </div>
                <div className="help-item">
                  <CheckCircle size={20} color="#10b981" />
                  <div>
                    <h4>Feedback & Suggestions</h4>
                    <ul>
                      <li>Ideas for new features</li>
                      <li>Content requests</li>
                      <li>Usability improvements</li>
                      <li>Design suggestions</li>
                    </ul>
                  </div>
                </div>
                <div className="help-item">
                  <CheckCircle size={20} color="#10b981" />
                  <div>
                    <h4>General Questions</h4>
                    <ul>
                      <li>How the calculator works</li>
                      <li>Privacy and security concerns</li>
                      <li>About our mission</li>
                      <li>Educational content questions</li>
                    </ul>
                  </div>
                </div>
                <div className="help-item">
                  <CheckCircle size={20} color="#10b981" />
                  <div>
                    <h4>Success Stories</h4>
                    <ul>
                      <li>Share your debt-free journey</li>
                      <li>Inspire others (with permission)</li>
                      <li>Feature requests based on experience</li>
                      <li>Testimonials and reviews</li>
                    </ul>
                  </div>
                </div>
              </div>
            </article>

            {/* Section 3: What We Can't Help With */}
            <article className="contact-section limitations-section">
              <div className="section-header">
                <XCircle size={28} color="#ef4444" />
                <h3>What We Can't Help With</h3>
              </div>
              <div className="limitations-box">
                <p className="limitations-intro">
                  While we'd love to help everyone, there are some areas outside our expertise. 
                  We're not licensed financial advisors and must maintain ethical boundaries.
                </p>
                <div className="limitation-items">
                  <div className="limitation-item">
                    <XCircle size={18} color="#ef4444" />
                    <div>
                      <h4>Personal Financial Advice</h4>
                      <p>
                        We cannot provide guidance specific to your financial situation, recommend 
                        investment strategies, or advise on whether to pay off debt vs. invest.
                      </p>
                    </div>
                  </div>
                  <div className="limitation-item">
                    <XCircle size={18} color="#ef4444" />
                    <div>
                      <h4>Debt Negotiation or Settlement</h4>
                      <p>
                        We don't negotiate with creditors, settle debts, or provide credit repair 
                        services. See resources below for professional help.
                      </p>
                    </div>
                  </div>
                  <div className="limitation-item">
                    <XCircle size={18} color="#ef4444" />
                    <div>
                      <h4>Legal or Tax Questions</h4>
                      <p>
                        We cannot provide legal advice, tax guidance, or help with bankruptcy 
                        decisions. Please consult appropriate licensed professionals.
                      </p>
                    </div>
                  </div>
                  <div className="limitation-item">
                    <XCircle size={18} color="#ef4444" />
                    <div>
                      <h4>Credit Score Issues</h4>
                      <p>
                        We don't have access to credit reports or scores and cannot help with 
                        disputes, credit building strategies, or reporting errors.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </article>

            {/* Section 4: Need Professional Help? */}
            <article className="contact-section resources-section">
              <div className="section-header">
                <Phone size={28} color="#3b82f6" />
                <h3>Need Professional Help?</h3>
              </div>
              <p className="resources-intro">
                If you need personalized financial advice, debt counseling, or legal assistance, 
                these trusted organizations offer free or low-cost services:
              </p>
              <div className="resources-grid">
                <div className="resource-card">
                  <h4>National Foundation for Credit Counseling (NFCC)</h4>
                  <p>
                    Nonprofit network of certified credit counselors offering free and low-cost 
                    financial guidance, debt management plans, and housing counseling.
                  </p>
                  <div className="resource-links">
                    <a 
                      href="https://www.nfcc.org/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="resource-link"
                    >
                      Visit Website
                      <ExternalLink size={14} />
                    </a>
                    <span className="resource-phone">📞 1-800-388-2227</span>
                  </div>
                </div>
                <div className="resource-card">
                  <h4>Financial Counseling Association of America (FCAA)</h4>
                  <p>
                    Professional organization of financial counselors providing education, 
                    counseling, and advocacy for consumer financial health.
                  </p>
                  <div className="resource-links">
                    <a 
                      href="https://www.financialcounselingassoc.org/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="resource-link"
                    >
                      Visit Website
                      <ExternalLink size={14} />
                    </a>
                  </div>
                </div>
                <div className="resource-card">
                  <h4>Consumer Financial Protection Bureau (CFPB)</h4>
                  <p>
                    U.S. government agency protecting consumers in the financial sector. 
                    Offers complaint resolution, educational resources, and regulatory oversight.
                  </p>
                  <div className="resource-links">
                    <a 
                      href="https://www.consumerfinance.gov/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="resource-link"
                    >
                      Visit Website
                      <ExternalLink size={14} />
                    </a>
                    <span className="resource-phone">📞 1-855-411-2372</span>
                  </div>
                </div>
                <div className="resource-card">
                  <h4>National Debt Relief</h4>
                  <p>
                    Accredited debt settlement company helping consumers negotiate with creditors 
                    to reduce debt amounts and create payment plans.
                  </p>
                  <div className="resource-links">
                    <a 
                      href="https://www.nationaldebtrelief.com/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="resource-link"
                    >
                      Visit Website
                      <ExternalLink size={14} />
                    </a>
                  </div>
                </div>
              </div>
              <div className="resources-note">
                <strong>Important:</strong> Always verify credentials and be wary of services 
                requiring large upfront fees. Legitimate credit counseling agencies typically 
                charge minimal or no fees for initial consultations.
              </div>
            </article>

            {/* Section 5: Before You Email */}
            <article className="contact-section tips-section">
              <div className="section-header">
                <Lightbulb size={28} color="#fbbf24" />
                <h3>Before You Email</h3>
              </div>
              <div className="tips-box">
                <p className="tips-intro">
                  To help us respond faster and more effectively, please consider these tips:
                </p>
                <div className="tips-grid">
                  <div className="tip-item">
                    <span className="tip-number">1</span>
                    <div>
                      <h4>Check the FAQ Section</h4>
                      <p>
                        Many common questions are already answered in our comprehensive FAQ. 
                        It might save you time!
                      </p>
                    </div>
                  </div>
                  <div className="tip-item">
                    <span className="tip-number">2</span>
                    <div>
                      <h4>Include Calculator Details</h4>
                      <p>
                        If reporting a bug, let us know what numbers you entered and what 
                        unexpected result you received.
                      </p>
                    </div>
                  </div>
                  <div className="tip-item">
                    <span className="tip-number">3</span>
                    <div>
                      <h4>Mention Your Browser</h4>
                      <p>
                        For technical issues, include your browser type and version 
                        (Chrome, Firefox, Safari, etc.).
                      </p>
                    </div>
                  </div>
                  <div className="tip-item">
                    <span className="tip-number">4</span>
                    <div>
                      <h4>Screenshots Help!</h4>
                      <p>
                        If you're experiencing a visual bug or error message, a screenshot 
                        helps us troubleshoot much faster.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </article>

            {/* Section 6: What to Expect */}
            <article className="contact-section expectations-section">
              <div className="section-header">
                <MessageCircle size={28} color="#8b5cf6" />
                <h3>What to Expect</h3>
              </div>
              <div className="expectations-content">
                <div className="expectation-item">
                  <div className="expectation-icon">📧</div>
                  <div>
                    <h4>We Read Everything</h4>
                    <p>
                      Every email is read personally by our team. We don't use auto-responders 
                      or outsource customer service.
                    </p>
                  </div>
                </div>
                <div className="expectation-item">
                  <div className="expectation-icon">⏰</div>
                  <div>
                    <h4>Response Times</h4>
                    <p>
                      Most emails receive a response within 24-48 hours. Bug reports and 
                      technical issues are prioritized.
                    </p>
                  </div>
                </div>
                <div className="expectation-item">
                  <div className="expectation-icon">💡</div>
                  <div>
                    <h4>Your Ideas Matter</h4>
                    <p>
                      We actively implement user suggestions. Many features exist because 
                      someone like you asked for them.
                    </p>
                  </div>
                </div>
                <div className="expectation-item">
                  <div className="expectation-icon">🙏</div>
                  <div>
                    <h4>Thank You!</h4>
                    <p>
                      We genuinely appreciate you taking the time to reach out. Your feedback 
                      makes this tool better for everyone.
                    </p>
                  </div>
                </div>
              </div>
            </article>

          </div>

          {/* Footer Note */}
          <div className="contact-footer">
            <p>
              <strong>Ready to reach out?</strong> Send your message to{' '}
              <a href="mailto:contact@zerodebtjourney.com">contact@zerodebtjourney.com</a>
            </p>
          </div>

        </div>

      </div>
    </section>
  )
}

export default ContactPage
